import { ComponentType } from 'react';

import { useSelector } from 'react-redux';

import { cn } from '@/lib/utils';

import { selectActiveTab, selectIsSending } from '../redux/selectors';
import { TabId } from '../types/state';
import { Header } from './Header/Header';
import { AuthenticationPanel } from './Panels/AuthenticationPanel/AuthenticationPanel';
import { ContextPanel } from './Panels/ContextPanel/ContextPanel';
import { ConversationPanelReplay } from './Panels/ConversationPanel/ConversationPanelReplay';
import { DetailsPanel } from './Panels/DetailsPanel/DetailsPanel';
import { ModelPanel } from './Panels/ModelPanel/ModelPanel';
import { ReviewPanel } from './Panels/ReviewPanel/ReviewPanel';
import { UserPanel } from './Panels/UserPanel/UserPanel';

type Props = { onClose: () => void; className?: string };

// Built on render, not at module scope: with a circular import, a module-level
// map would read a panel inside its temporal dead zone and throw on import.
const panelForTab = (tab: TabId): ComponentType => {
  const panels: Record<TabId, ComponentType> = {
    [TabId.Details]: DetailsPanel,
    [TabId.Context]: ContextPanel,
    [TabId.Conversation]: ConversationPanelReplay,
    [TabId.User]: UserPanel,
    [TabId.Review]: ReviewPanel,
    [TabId.Authentication]: AuthenticationPanel,
    [TabId.Model]: ModelPanel,
  };
  return panels[tab] ?? DetailsPanel;
};

/**
 * Right column shell: the tab/close header plus the panel for the active tab.
 * The panels read what they need from the slice themselves.
 */
export const RightSide = ({ onClose, className }: Props) => {
  const activeTab = useSelector(selectActiveTab);
  const isSending = useSelector(selectIsSending);
  const Panel = panelForTab(activeTab);

  return (
    <section className={cn('flex min-w-0 flex-1 basis-0 flex-col overflow-hidden', className)}>
      <Header onClose={onClose} />
      {/* Mounted always (hidden while sending) so in-flight draft context rows survive the send. */}
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col items-start gap-6 self-stretch overflow-y-auto p-6',
          isSending && 'hidden'
        )}
      >
        <Panel />
      </div>
    </section>
  );
};
